import React, {
  useState, useEffect, useContext, useRef,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { ErrorBoundary } from 'react-error-boundary';

import { Button } from '@mui/material';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Snackbar from '@mui/material/Snackbar';
import { Star, StarBorder } from '@mui/icons-material';
import Tooltip from '@mui/material/Tooltip';
import {
  logReactErrBoundaryError, clientSettingsUpdater, decimalFormatter, validateProportion,
} from '../utils';
import APIWrapper from '../utils/graphqlwrapper';
import logger from '../utils/logger';
import { alphabetizeCols } from '../utils/tableHelpers';

import GeneralTab from '../components/client/GeneralTab';
import DataIngest from '../components/client/DataIngest';
import AdAITab from '../components/client/AdAITab';
import AdsTab from '../components/client/AdsTab';
import OtherInfoTab from '../components/client/OtherInfoTab';
import Alert from '../utils/Alert';
import TabPanel from '../components/TabPanel';
import FallbackOnError from '../components/FallbackOnError';
import {
  addFavoriteClient,
  checkIfFavorite,
  getFavoriteClients,
  removeFavoriteClient,
} from '../utils/favsAndRecentClients';
import AppContext from '../context';
import { defaultStructure } from '../utils/settingConstants';

function FavoriteCheckbox({ isChecked, ...props }) {
  if (typeof isChecked === 'boolean') {
    return (
      <Checkbox
        icon={<StarBorder />}
        checkedIcon={<Star />}
        checked={isChecked}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
      />
    );
  }

  return (
    <Checkbox
      icon={<StarBorder />}
      checkedIcon={<Star />}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    />
  );
}

FavoriteCheckbox.propTypes = {
  isChecked: PropTypes.bool,
};

function ClientInfo(props) {
  const { authenticated, setShouldLogOut, setFavClients } = useContext(AppContext);
  // prop for new client
  const param = useParams();
  const {
    hasTDCAdminPermission,
    hasDataEngineerPermission,
    hasAnalystPermission,
    setHeader,
    isNew,
  } = props;
  const navigate = useNavigate();

  const [data, setData] = useState();
  const dataRef = useRef(data); // Create a ref to hold the latest state
  const [timeStamps, setTimeStamps] = useState();
  const [alertMessage, setAlertMessage] = useState();
  const [structure, setStructure] = useState({});
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackMessage, setSnackMessage] = useState('');
  const [permissions, setPermissions] = useState({});
  const [tab, setTab] = useState(0);
  const [isFormValid, setIsFormValid] = useState(true);
  const [createdAt, setCreatedAt] = useState(null);
  const [vendorNames, setVendorNames] = useState([]);
  const [isLoadingMMClients, setIsLoadingMMClients] = useState(true);

  const pageSetter = setHeader;
  let formatMissingMessage;
  let formatTabMessage;

  // used for Snackbar
  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackbar(false);
    setSnackMessage('');
  };

  const cleanConfigData = (configArr) => {
    // function to clean and format email/sms config data before sending to db
    const configIntFields = ['rank', 'days'];

    let parsedConfig = configArr;
    // if config is a string, parse it
    if (typeof parsedConfig === 'string') {
      parsedConfig = JSON.parse(parsedConfig);
    }
    // order config objects by rank ascending
    parsedConfig.sort((a, b) => a.rank - b.rank);

    if (parsedConfig.length > 0) {
      // map through array and format number / decimal fields
      parsedConfig = parsedConfig.map((configData) => {
        const updatedConfig = { ...configData };
        if (updatedConfig.proportion) {
          updatedConfig.proportion = decimalFormatter(updatedConfig.proportion);
        }
        configIntFields.forEach((field) => {
          // check if the field exists in `config` or `config.details`
          if (updatedConfig[field] !== undefined) {
            updatedConfig[field] = parseInt(updatedConfig[field], 10);
          } else if (updatedConfig.details && updatedConfig.details[field] !== undefined) {
            if (field === 'days' && updatedConfig.details[field] === 'Select Donor Recency') {
              delete updatedConfig.details[field];
            } else {
              updatedConfig.details[field] = parseInt(updatedConfig.details[field], 10);
            }
          }
        });
        return updatedConfig;
      });
      // then stringify the array for the db
      parsedConfig = JSON.stringify(parsedConfig);
    } else {
      parsedConfig = JSON.stringify(parsedConfig);
    }
    return parsedConfig;
  };

  const cleanData = (apidata) => {
    const clean = apidata;
    ['updatedAt', 'createdAt', '_lastChangedAt', '_deleted'].forEach((item) => {
      if (clean[item]) delete clean[item];
    });
    // if the platform fields are not yet set, set it as an empty array.
    [
      'transaction_platform',
      'people_platform',
      'action_platform',
      'exhaust_platform',
      'sms_platform',
      'transactions_offline_platform',
    ].forEach((item) => {
      if (clean[item] === null) clean[item] = [];
    });

    // clean and format email and sms config data
    if (clean.email_configurations) {
      clean.email_configurations = cleanConfigData(clean.email_configurations);
    }
    if (clean.sms_configurations) {
      clean.sms_configurations = cleanConfigData(clean.sms_configurations);
    }

    return clean;
  };

  const getClientInfo = async (clientId) => {
    const result = await APIWrapper.queryApi({ query: 'getClient' }, setShouldLogOut, {
      client_id: clientId,
    })
      .then((resp) => resp)
      .catch((err) => logger.error(err));
    return result;
  };

  const getClients = async () => {
    const result = await APIWrapper.queryApi(
      { query: 'listClients' },
      setShouldLogOut,
      { limit: 1000 },
    )
      .then((resp) => resp)
      .catch((err) => logger.error(err));
    return result;
  };

  // get timestamps
  const timestampfields = ['featurize', 'is_active', 'is_advantage_member'];
  const getTimeStampInfo = async (clientId) => {
    // eslint-disable-next-line max-len
    const timestampqueries = timestampfields.map((field) => APIWrapper.queryApi({ query: 'searchTimeStamps' }, false, {
      filter: { client_id: { eq: clientId }, column_name: { eq: field } },
      sort: { direction: 'desc', field: 'createdAt' },
      limit: 1,
    })
      .then((resp) => resp)
      .catch((err) => logger.error(err)));

    const results = await Promise.allSettled(timestampqueries).catch((err) => {
      logger.error(err);
    });
    const formattedResults = {};
    results.forEach((r, i) => {
      if (r.value) {
        // returns single obj with changed time
        const result = r.value.data[0][0];
        formattedResults[timestampfields[i]] = result;
      }
    });

    setTimeStamps(formattedResults);
  };

  useEffect(() => {
    // if we're making a new client set the header but don't fetch data
    if (isNew) {
      setData({});
      dataRef.current = {};
      if (typeof pageSetter === 'function') {
        document.title = 'MissionPortal | New Client';
        pageSetter(['New Client']);
      }
    } else {
      document.title = `MissionPortal | ${param.client}`;
      getClientInfo(param.client)
        .then((resp) => {
          const [clientInfo] = resp.data[0];
          setData(clientInfo);
          dataRef.current = clientInfo;
          setCreatedAt(clientInfo.createdAt);
        })
        .catch((err) => {
          logger.error(err);
        });
    }

    // Initialize structure with loading state (only if not new client)
    const initialStructure = { ...defaultStructure };
    initialStructure.multi_member_clients = ['loading...'];
    initialStructure.multi_member_sms_clients = ['loading...'];
    initialStructure.multi_member_mid_level_clients = ['loading...'];
    setStructure(initialStructure);

    // Fetch clients and update structure when complete
    let vendorOptions = [];
    const adAiClientNames = [''];

    getClients().then((resp) => {
      // need to filter out clients that have is_advantage_member set to null or false
      const adAiClients = resp.data[0].filter((client) => client.is_advantage_member === true);
      vendorOptions = [
        ...new Set(
          resp.data[0]
            .map((client) => client.vendor)
            .filter((vendor) => vendor !== null && vendor !== undefined),
        ),
      ];
      setVendorNames(vendorOptions);
      // alphabetize client_full_name in adai list for multi-member clients dropdowns
      const alphabetizedCols = alphabetizeCols(adAiClients, 'client_full_name');

      alphabetizedCols.forEach((client) => {
        adAiClientNames.push(client.client_full_name);
      });

      // Update structure with the loaded client names
      const updatedStructure = { ...defaultStructure };
      updatedStructure.multi_member_clients = adAiClientNames;
      updatedStructure.multi_member_sms_clients = adAiClientNames;
      updatedStructure.multi_member_mid_level_clients = adAiClientNames;
      setStructure(updatedStructure);
      setIsLoadingMMClients(false); // Mark loading as complete
    }).catch((err) => {
      logger.error(err);
      // Fallback to default structure on error
      setStructure({ ...defaultStructure });
      setIsLoadingMMClients(false); // Mark loading as complete even on error
    });

    // get client timestamp information for specific fields
    getTimeStampInfo(param.client);
    // need to validate permissions prop for nested components and it is looking in this file.
    setPermissions({});
    // get user permission info (about what they can edit here)
  }, []);

  const handleChange = (event, newValue) => {
    setTab(newValue);
  };

  const updateData = (attribute, value, label, index) => {
    let valueToUpdate = value;

    if (attribute === 'client_id' && typeof value === 'string') {
      valueToUpdate = value.replace(/\s+/g, '').toLowerCase();
    }
    const updated = clientSettingsUpdater(dataRef.current, attribute, valueToUpdate, label, index);
    logger.debug(updated);
    setData({ ...updated });
    dataRef.current = updated;
  };

  const updateValidity = (bool) => {
    if (bool === false) {
      setAlertMessage({
        severity: 'error',
        message: 'Please ensure all changed fields have valid inputs.',
      });
    } else {
      setAlertMessage();
    }
    setIsFormValid(bool);
  };

  const validateConfigRank = (rankArr) => {
    const rankErrors = [];
    // check that ranks aren't duplicated or skipped
    const sorted = rankArr.sort((a, b) => a - b);
    for (let i = 0; i < sorted.length; i += 1) {
      if (sorted[i] !== i + 1) {
        rankErrors.push('Donor config ranks must be sequential and not duplicated.');
        break;
      }
    }
    return rankErrors;
  };

  const checkRankOrMissing = (configArr, missingArr, rankArr) => {
    // function to check if any required fields for email/sms config are missing
    // or if ranks are duplicated/skipped
    const value = configArr;
    const resetFields = ['Select Donor Config Type', 'Select Donor Category'];
    const requiredField = [
      'rank',
      'type',
      'proportion',
    ];

    const nestedFields = [
      'donor_type',
    ];
    // check for missing required fields
    value.forEach((config) => {
      requiredField.forEach((att, i) => {
        if (!(att in config) || config[att] === '' || config[att] === null || resetFields.includes(config[att])) {
          missingArr.push(requiredField[i]);
        }
      });
      nestedFields.forEach((att, i) => {
        if (!(att in config.details) || config.details[att] === '' || config.details[att] === null || Object.keys(config.details).length === 0 || resetFields.includes(config.details[att])) {
          missingArr.push(nestedFields[i]);
        }
      });
      rankArr.push(Number(config.rank));
    });
    // validate ranks
    const ranks = validateConfigRank(rankArr);
    return {
      missing: missingArr,
      ranks,
    };
  };

  const validateConfigValues = (email, sms) => {
    /**
     * Function to validate email and sms config values
     * It checks for missing required fields and validates ranks and proportion values
     * @returns {object} - An object containing validation results for email and sms configurations
     */
    const emailMissing = [];
    const smsMissing = [];
    const emailRanks = [];
    const smsRanks = [];
    let isEmailProportionValid = true;
    let isSmsProportionValid = true;
    let emailResults;
    let smsResults;
    let formattedEmail;
    let formattedSms;

    if (email) {
      formattedEmail = (typeof email === 'string') ? JSON.parse(email) : email;
      // check if ranks are valid and if any required fields are missing
      emailResults = checkRankOrMissing(formattedEmail, emailMissing, emailRanks);
      // iterate through each email config and validate proportion values
      isEmailProportionValid = formattedEmail.every(
        (config) => !config.proportion || validateProportion(config.proportion),
      );
    } if (sms) {
      formattedSms = (typeof sms === 'string') ? JSON.parse(sms) : sms;
      // check if ranks are valid and if any required fields are missing
      smsResults = checkRankOrMissing(formattedSms, smsMissing, smsRanks);
      // iterate through each sms config and validate proportion values
      isSmsProportionValid = formattedSms.every(
        (config) => !config.proportion || validateProportion(config.proportion),
      );
    }
    return {
      emailResults,
      smsResults,
      isEmailProportionValid,
      isSmsProportionValid,
    };
  };

  const validateForm = () => {
    const required = [
      'client_full_name',
      'client_id',
      'jira_project_name',
      'client_type',
      'sector',
    ];
    const fields = [
      'client full name',
      'client code',
      'Jira project name',
      'client type',
      'sector',
    ];
    const missing = [];
    const missingLocation = [];
    // record which required fields are missing and which tab they belong to
    required.forEach((att, i) => {
      if (!(att in data)) {
        if (isNew && att === 'sector') {
          missing.push(fields[i]);
          missingLocation.push('Other');
        } else if (att !== 'sector') {
          missing.push(fields[i]);
          missingLocation.push('General');
        }
      }
    });
    return { missing, missingLocation };
  };

  const onSubmit = async () => {
    /**
     * Function to handle the submission of client data.
     * It validates the form, checks if the client already exists,
     * and either updates an existing client or creates a new one.
     * Also triggers the creation of Files.com folders and groups.
     * @returns {void}
     */
    if (authenticated) {
      const validatedValues = validateForm();
      if (validatedValues.missing.length > 0) {
        formatMissingMessage = validatedValues.missing.join(', ');
        // deduplicate tab names
        const uniqueTabsSet = new Set(validatedValues.missingLocation);
        const dedupedTabs = [...uniqueTabsSet];
        // if there's more than one tab with missing fields, use plural 'tabs' and format with 'and'
        formatTabMessage = dedupedTabs.join(' and ');
        const tabText = dedupedTabs.length > 1 ? 'tabs' : 'tab';
        // show alert message for missing required fields
        setAlertMessage({
          severity: 'error',
          message:
            `Please fill out all required fields. Missing ${formatMissingMessage} in the ${formatTabMessage} ${tabText}.`,
        });
        // eslint-disable-line
        return;
      }
      // validate email and sms config data if it exists
      let formatMessage;
      if (data.email_configurations || data.sms_configurations) {
        const validate = validateConfigValues(data.email_configurations, data.sms_configurations);
        if (validate.emailResults && validate.emailResults.missing.length > 0) {
          formatMessage = validate.emailResults.missing.join(', ');
        } else if (validate.smsResults && validate.smsResults.missing.length > 0) {
          formatMessage = validate.smsResults.missing.join(', ');
        }
        // required fields are missing return error
        if (formatMessage !== undefined) {
          setAlertMessage({
            severity: 'error',
            message:
              `Please fill out all required fields for the donor configuration. Missing ${formatMessage}.`,
          });
          // eslint-disable-line
          return;
        }
        // ranks are duplicated or skipped return error
        if (
          (validate.emailResults && validate.emailResults.ranks.length > 0)
          || (validate.smsResults && validate.smsResults.ranks.length > 0)
        ) {
          setAlertMessage({
            severity: 'error',
            message:
              'Donor config ranks must be sequential and not duplicated.',
          });
          // eslint-disable-line
          return;
        }
        if ((!validate.isEmailProportionValid)
          || (!validate.isSmsProportionValid)) {
          setAlertMessage({
            severity: 'error',
            message:
              'Proportion value is not valid. Please ensure it is a decimal between 0.01 and 0.99 (1% - 99%).',
          });
          // eslint-disable-line
          return;
        }
      }
      // clean data before sending to api
      const cleanedData = cleanData(data);
      if (!isNew) {
        // update the current client
        APIWrapper.queryApi(
          { mutation: 'updateClient' },
          setShouldLogOut,
          { input: cleanedData },
        )
          .then((resp) => {
            // Check for GraphQL errors (including Unauthorized)
            if (resp.errors && resp.errors.length > 0) {
              const error = resp.errors[0];
              logger.error('GraphQL error:', error);
              setSnackMessage(error.message || 'Error updating client information.');
              setOpenSnackbar(true);
              return;
            }
            const [updatedClient] = resp.data[0];
            setData(updatedClient);
            dataRef.current = updatedClient;
            setSnackMessage('Client information updated.');
            setOpenSnackbar(true);
          })
          .catch((err) => {
            logger.error(err);
            setSnackMessage(err.message || 'Error updating client information.');
            setOpenSnackbar(true);
          });
      } else {
        // client_id is lowercase and trimmed
        // check that client we want to create does not exist in database already
        getClientInfo(cleanedData.client_id)
          .then((client) => {
            if (client.data[0].length === 0) {
              // new client
              APIWrapper.queryApi(
                { mutation: 'createClient' },
                setShouldLogOut,
                { input: cleanedData },
              )
                .then((resp) => {
                  // Check for GraphQL errors (including Unauthorized)
                  if (resp.errors && resp.errors.length > 0) {
                    const error = resp.errors[0];
                    logger.error('GraphQL error:', error);
                    setSnackMessage(error.message || 'Error creating client information.');
                    setOpenSnackbar(true);
                    return;
                  }
                  const newClient = resp.data[0];
                  const cleanedClient = cleanData(newClient[0]);
                  setData(cleanedClient);
                  dataRef.current = cleanedClient;
                  navigate(`/client/${cleanedClient.client_id}`, {
                    replace: false,
                    state: { client: cleanedClient },
                  });
                })
                .catch((err) => {
                  logger.error(err);
                  setSnackMessage(err.message || 'Error creating client information.');
                  setOpenSnackbar(true);
                });
              // create files.com folders and groups
              const input = JSON.stringify({
                vendor: data.vendor || null,
                clientFullName: data.client_full_name,
                strategy: data.is_strategy_partner || null,
              });
              APIWrapper.queryApi(
                { query: 'filesDotComTrigger' },
                setShouldLogOut,
                { input },
              )
                .then(() => { })
                .catch((err) => {
                  logger.error(err);
                });
            } else {
              setAlertMessage({
                severity: 'error',
                message: `Client ${data.client_id} already exists.`,
              });
            }
          })
          .catch((err) => {
            logger.error(err);
          });
      }
    }

    // timestamp lambda takes a second or two to update the table so wait then refetch timestamps
    setTimeout(() => {
      getTimeStampInfo(data.client_id);
    }, 5000);
  };

  const updateFavorites = () => {
    const checkFavorited = checkIfFavorite(param.client);
    if (checkFavorited) {
      removeFavoriteClient(param.client);
      setFavClients(getFavoriteClients());
    } else {
      addFavoriteClient(param.client);
      setFavClients(getFavoriteClients());
    }
  };

  return (
    data && (
      <ErrorBoundary
        FallbackComponent={FallbackOnError}
        onError={logReactErrBoundaryError}
        onReset={(details) => {
          logger.info('details', details);
          // Reset the state of your app so the error doesn't happen again
        }}
      >
        <div>
          <header className="App-header" id="clientinfo-header">
            <div id="clientinfo-link">
              <h2>
                {isNew
                  ? 'Create Client'
                  : `Manage ${data.client_id.toUpperCase()}`}
              </h2>
              {!isNew && (
                <FavoriteCheckbox
                  checked={checkIfFavorite(param.client)}
                  onClick={updateFavorites}
                />
              )}
            </div>
            {(hasTDCAdminPermission || hasDataEngineerPermission || hasAnalystPermission) && (
              <div id="submit-clientinfo">
                <Button size="small" variant="contained" onClick={onSubmit} disabled={!isFormValid}>
                  Submit
                </Button>
              </div>
            )}
          </header>
          <Box sx={{ width: '100%' }}>
            <>
              <div id="alert-message">
                {alertMessage
                  && (
                    <Alert
                      alert={alertMessage}
                    />
                  )}
              </div>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                {
                  (!hasTDCAdminPermission
                    && !hasDataEngineerPermission
                    && !hasAnalystPermission) && (
                    <Tabs
                      value={tab}
                      onChange={handleChange}
                      aria-label="basic tabs example"
                    >
                      <Tab label="General" />
                      <Tooltip title="You do not have permission to edit this tab" placement="bottom">
                        <div>
                          <Tab label="Data Ingest" disabled />
                        </div>
                      </Tooltip>
                      <Tooltip title="You do not have permission to edit this tab" placement="bottom">
                        <div>
                          <Tab label="Advantage AI" disabled />
                        </div>
                      </Tooltip>
                      <Tooltip title="You do not have permission to edit this tab" placement="bottom">
                        <div>
                          <Tab label="Ads" disabled />
                        </div>
                      </Tooltip>
                      <Tooltip title="You do not have permission to edit this tab" placement="bottom">
                        <div>
                          <Tab label="Other" disabled />
                        </div>
                      </Tooltip>
                    </Tabs>
                  )
                }
                {(hasDataEngineerPermission && !hasTDCAdminPermission && !hasAnalystPermission) && (
                  <Tabs
                    value={tab}
                    onChange={handleChange}
                    aria-label="basic tabs example"
                  >
                    <Tab label="General" />
                    <Tab label="Data Ingest" />
                    <Tooltip title="You do not have permission to edit this tab" placement="bottom">
                      <div>
                        <Tab label="Advantage AI" disabled />
                      </div>
                    </Tooltip>
                    <Tab label="Ads" />
                    <Tooltip title="You do not have permission to edit this tab" placement="bottom">
                      <div>
                        <Tab label="Other" disabled />
                      </div>
                    </Tooltip>
                  </Tabs>
                )}
                {(!hasDataEngineerPermission && !hasTDCAdminPermission && hasAnalystPermission) && (
                  <Tabs
                    value={tab}
                    onChange={handleChange}
                    aria-label="basic tabs example"
                  >
                    <Tab label="General" />
                    <Tooltip title="You do not have permission to edit this tab" placement="bottom">
                      <div>
                        <Tab label="Data Ingest" disabled />
                      </div>
                    </Tooltip>
                    <Tooltip title="You do not have permission to edit this tab" placement="bottom">
                      <div>
                        <Tab label="Advantage AI" disabled />
                      </div>
                    </Tooltip>
                    <Tab label="Ads" />
                    <Tab label="Other" />
                  </Tabs>
                )}
                {hasTDCAdminPermission && (
                  <Tabs
                    value={tab}
                    onChange={handleChange}
                    aria-label="basic tabs example"
                  >
                    <Tab label="General" />
                    <Tab label="Data Ingest" />
                    <Tab label="Advantage AI" />
                    <Tab label="Ads" />
                    <Tab label="Other" />
                  </Tabs>
                )}
              </Box>
              <>
                <TabPanel label="General" value={tab} index={0}>
                  <GeneralTab
                    setFunction={updateData}
                    data={data}
                    structure={structure}
                    key={data}
                    permissions={permissions}
                    isNew={isNew}
                    hasTDCAdminPermission={hasTDCAdminPermission}
                    vendorNames={vendorNames}
                  />
                </TabPanel>
                <TabPanel value={tab} index={1}>
                  <DataIngest
                    validateFunc={updateValidity}
                    setFunction={updateData}
                    data={data}
                    structure={structure}
                    key={data}
                    permissions={permissions}
                    timeStamps={timeStamps}
                    createdAt={createdAt}
                  />
                </TabPanel>
                <TabPanel value={tab} index={2}>
                  <AdAITab
                    validateFunc={updateValidity}
                    setFunction={updateData}
                    data={data}
                    structure={structure}
                    key={data}
                    hasTDCAdminPermission={hasTDCAdminPermission}
                    permissions={permissions}
                    timeStamps={timeStamps}
                    createdAt={createdAt}
                    isLoadingMMClients={isLoadingMMClients}
                  />
                </TabPanel>
                <TabPanel value={tab} index={3}>
                  <AdsTab
                    validateFunc={updateValidity}
                    setFunction={updateData}
                    data={data}
                    structure={structure}
                    key={data}
                    permissions={permissions}
                  />
                </TabPanel>
                <TabPanel value={tab} index={4}>
                  <OtherInfoTab
                    setFunction={updateData}
                    data={data}
                    structure={structure}
                    key={data}
                    permissions={permissions}
                  />
                </TabPanel>
              </>
            </>
          </Box>
        </div>
        <Snackbar
          open={openSnackbar}
          autoHideDuration={3000}
          onClose={handleClose}
          message={snackMessage}
        />
      </ErrorBoundary>
    )
  );
}

export default ClientInfo;

ClientInfo.propTypes = {
  hasTDCAdminPermission: PropTypes.bool.isRequired,
  hasDataEngineerPermission: PropTypes.bool.isRequired,
  hasAnalystPermission: PropTypes.bool.isRequired,
  isNew: PropTypes.bool,
  setHeader: PropTypes.func,
};
